import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import { toDateOnlyString } from '../../utils/dateUtils';

interface GeneratedLink {
  name: string;
  email: string;
  uniqueId: string;
  link: string;
  alreadyExists: boolean;
}

interface ChristmasEntry {
  clientName: string;
  childName: string;
  childAge: number;
  childGender: 'boy' | 'girl' | 'prefer-not-to-say';
  toyPreference?: string;
}

interface Submission {
  uniqueId: string;
  counsellorName: string;
  counsellorEmail?: string;
  entriesCount: number;
  entries: ChristmasEntry[];
  lastUpdated: string;
}

interface SubmissionsData {
  submissions: Submission[];
  totalCounsellors: number;
  totalEntries: number;
  counsellorsSubmitted: number;
}

const ChristmasAdmin: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Generate Links Tab
  const [counsellorInput, setCounsellorInput] = useState('');
  const [expiryDate, setExpiryDate] = useState<string>(() => {
    // Default to 30 days from now
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return toDateOnlyString(date);
  });
  const [sendEmails, setSendEmails] = useState(false);
  const [generatedLinks, setGeneratedLinks] = useState<GeneratedLink[]>([]);
  
  // View Submissions Tab
  const [submissions, setSubmissions] = useState<SubmissionsData | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    // Fetch submissions on mount to show generated links
    fetchSubmissions();
  }, []);

  useEffect(() => {
    if (tabValue === 1) {
      fetchSubmissions();
    }
  }, [tabValue]);

  const handleGenerateLinks = async () => {
    setError('');
    setSuccess('');
    
    // Parse input - expect format: "Name <email@example.com>" per line
    const lines = counsellorInput.trim().split('\n').filter(line => line.trim());
    
    if (lines.length === 0) {
      setError('Please enter at least one counsellor');
      return;
    }

    const counsellors = lines.map(line => {
      const match = line.match(/^(.+?)\s*<(.+?)>$/);
      if (match) {
        return { name: match[1].trim(), email: match[2].trim() };
      }
      // If no email format, treat whole line as name
      return { name: line.trim(), email: '' };
    });

    try {
      setLoading(true);
      const response = await api.post('/api/christmas/generate-links', { 
        counsellors,
        // End of the selected day (UTC) so links stay valid through the expiry date
        expiryDate: new Date(`${expiryDate}T23:59:59.999Z`).toISOString(),
        sendEmails
      });
      setGeneratedLinks(response.data.links);
      
      let successMessage = `Generated ${response.data.links.length} unique links (expires ${new Date(expiryDate).toLocaleDateString()})`;
      
      if (sendEmails) {
        successMessage += `. Sent ${response.data.emailsSent} emails`;
        if (response.data.emailErrors && response.data.emailErrors.length > 0) {
          successMessage += ` (${response.data.emailErrors.length} failed)`;
          console.error('Email errors:', response.data.emailErrors);
        }
      }
      
      setSuccess(successMessage + '!');
      setCounsellorInput('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate links');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/christmas/admin/all-submissions');
      setSubmissions(response.data);
      
      // Convert submissions to generated links format for persistence
      if (response.data.submissions && response.data.submissions.length > 0) {
        const links = response.data.submissions.map((sub: Submission) => ({
          name: sub.counsellorName,
          email: sub.counsellorEmail || '',
          uniqueId: sub.uniqueId,
          link: `${window.location.origin}/christmas/${sub.uniqueId}`,
          alreadyExists: true
        }));
        setGeneratedLinks(links);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch submissions');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard!');
    setTimeout(() => setSuccess(''), 2000);
  };

  const copyAllLinks = () => {
    const allLinks = generatedLinks
      .map(link => `${link.name} (${link.email}): ${link.link}`)
      .join('\n\n');
    copyToClipboard(allLinks);
  };

  const exportToCSV = () => {
    if (!submissions) return;

    const rows = [
      ['Counsellor Name', 'Counsellor Email', 'Client Name', 'Child Name', 'Child Age', 'Child Gender', 'Toy Preference']
    ];

    submissions.submissions.forEach(sub => {
      sub.entries.forEach(entry => {
        rows.push([
          sub.counsellorName,
          sub.counsellorEmail || '',
          entry.clientName,
          entry.childName,
          entry.childAge.toString(),
          entry.childGender === 'prefer-not-to-say' ? 'Prefer not to say' : entry.childGender.charAt(0).toUpperCase() + entry.childGender.slice(1),
          entry.toyPreference || ''
        ]);
      });
    });

    const csvContent = rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `christmas-submissions-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const viewDetails = (submission: Submission) => {
    setSelectedSubmission(submission);
    setDetailsOpen(true);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        🎄 Christmas Gift Collection
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'rgba(255, 255, 255, 0.2)', mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{
            '& .MuiTab-root': {
              color: 'rgba(255, 255, 255, 0.6)',
              '&.Mui-selected': {
                color: '#60a5fa'
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#60a5fa'
            }
          }}
        >
          <Tab label="Generate Links" />
          <Tab label="View Submissions" />
        </Tabs>
      </Box>

      {/* Generate Links Tab */}
      {tabValue === 0 && (
        <Paper sx={{ p: 3, backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)' }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
            Generate Unique Links for Counsellors
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
            Enter counsellor details (one per line). Format: Name &lt;email@example.com&gt;
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={10}
            value={counsellorInput}
            onChange={(e) => setCounsellorInput(e.target.value)}
            placeholder="John Doe <john@example.com>&#10;Jane Smith <jane@example.com>"
            sx={{ 
              mb: 2,
              '& .MuiInputBase-root': {
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.2)'
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'rgba(255, 255, 255, 0.5)',
                opacity: 1
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.2)'
              },
              '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          />

          <TextField
            fullWidth
            type="date"
            label="Link Expiry Date"
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ 
              mb: 2,
              '& .MuiInputBase-root': {
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.2)'
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.2)'
              },
              '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={sendEmails}
                onChange={(e) => setSendEmails(e.target.checked)}
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-checked': {
                    color: '#60a5fa'
                  }
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                <Typography sx={{ color: 'white' }}>Send emails to counsellors</Typography>
              </Box>
            }
            sx={{ mb: 2 }}
          />

          <Button
            variant="contained"
            onClick={handleGenerateLinks}
            disabled={loading || !counsellorInput.trim()}
            fullWidth
            startIcon={sendEmails ? <EmailIcon /> : undefined}
          >
            {loading ? <CircularProgress size={24} /> : sendEmails ? 'Generate Links & Send Emails' : 'Generate Links'}
          </Button>

          {generatedLinks.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: 'white' }}>
                  Generated Links ({generatedLinks.length})
                </Typography>
                <Button
                  startIcon={<CopyIcon />}
                  onClick={copyAllLinks}
                  size="small"
                  sx={{ color: '#60a5fa' }}
                >
                  Copy All
                </Button>
              </Box>

              <TableContainer sx={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.1)' }}>Name</TableCell>
                      <TableCell sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.1)' }}>Email</TableCell>
                      <TableCell sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.1)' }}>Link</TableCell>
                      <TableCell sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.1)' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {generatedLinks.map((link, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.1)' }}>{link.name}</TableCell>
                        <TableCell sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.1)' }}>{link.email}</TableCell>
                        <TableCell sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#60a5fa' }}>
                            {link.link}
                          </Typography>
                          {link.alreadyExists && (
                            <Chip label="Existing" size="small" color="info" sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard(link.link)}
                            title="Copy link"
                            sx={{ color: 'white' }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Paper>
      )}

      {/* View Submissions Tab */}
      {tabValue === 1 && (
        <Paper sx={{ p: 3, backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ color: 'white' }}>
              Submissions Overview
            </Typography>
            <Box>
              <Button
                startIcon={<DownloadIcon />}
                onClick={exportToCSV}
                disabled={!submissions || submissions.totalEntries === 0}
                sx={{ mr: 1 }}
              >
                Export CSV
              </Button>
              <IconButton onClick={fetchSubmissions} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>

          {loading && <CircularProgress />}

          {submissions && (() => {
            // Calculate detailed gender and age statistics
            const allEntries = submissions.submissions.flatMap(sub => sub.entries);
            
            // Age groups: 0-5, 6-10, 11-15, 16-18
            const ageGroups = [
              { label: '0-5 years', min: 0, max: 5 },
              { label: '6-10 years', min: 6, max: 10 },
              { label: '11-15 years', min: 11, max: 15 },
              { label: '16-18 years', min: 16, max: 18 }
            ];

            // Calculate breakdown by age and gender
            const breakdown = ageGroups.map(group => {
              const entriesInGroup = allEntries.filter(e => e.childAge >= group.min && e.childAge <= group.max);
              const boys = entriesInGroup.filter(e => e.childGender === 'boy').length;
              const girls = entriesInGroup.filter(e => e.childGender === 'girl').length;
              const preferNotToSay = entriesInGroup.filter(e => e.childGender === 'prefer-not-to-say').length;
              
              return {
                ...group,
                boys,
                girls,
                preferNotToSay,
                total: entriesInGroup.length
              };
            });

            const totalBoys = allEntries.filter(e => e.childGender === 'boy').length;
            const totalGirls = allEntries.filter(e => e.childGender === 'girl').length;
            const totalPreferNotToSay = allEntries.filter(e => e.childGender === 'prefer-not-to-say').length;

            return (
            <>
              {/* Main Stats */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Paper sx={{ p: 2, flex: 1, backgroundColor: 'rgba(96, 165, 250, 0.1)', border: '1px solid rgba(96, 165, 250, 0.3)' }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Total Counsellors
                  </Typography>
                  <Typography variant="h4" sx={{ color: 'white' }}>{submissions.totalCounsellors}</Typography>
                </Paper>
                <Paper sx={{ p: 2, flex: 1, backgroundColor: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Submitted
                  </Typography>
                  <Typography variant="h4" sx={{ color: 'white' }}>{submissions.counsellorsSubmitted}</Typography>
                </Paper>
                <Paper sx={{ p: 2, flex: 1, backgroundColor: 'rgba(168, 85, 247, 0.1)', border: '1px solid rgba(168, 85, 247, 0.3)' }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Total Children
                  </Typography>
                  <Typography variant="h4" sx={{ color: 'white' }}>{submissions.totalEntries}</Typography>
                </Paper>
              </Box>

              {/* Detailed Breakdown by Age and Gender */}
              <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>Children Breakdown by Age & Gender</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 2, mb: 3 }}>
                {breakdown.map((group, index) => {
                  const colors = [
                    'rgba(251, 191, 36, 0.1)', // Yellow for 0-5
                    'rgba(34, 197, 94, 0.1)',  // Green for 6-10
                    'rgba(59, 130, 246, 0.1)', // Blue for 11-15
                    'rgba(168, 85, 247, 0.1)'  // Purple for 16-18
                  ];
                  const borderColors = [
                    'rgba(251, 191, 36, 0.3)',
                    'rgba(34, 197, 94, 0.3)',
                    'rgba(59, 130, 246, 0.3)',
                    'rgba(168, 85, 247, 0.3)'
                  ];
                  
                  return (
                    <Paper 
                      key={group.label} 
                      sx={{ 
                        p: 2, 
                        backgroundColor: colors[index], 
                        border: `1px solid ${borderColors[index]}` 
                      }}
                    >
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1, fontWeight: 600 }}>
                        {group.label}
                      </Typography>
                      <Typography variant="h4" sx={{ color: 'white', mb: 2 }}>
                        {group.total} {group.total === 1 ? 'child' : 'children'}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {group.boys > 0 && (
                          <Typography variant="body2" sx={{ color: 'rgba(59, 130, 246, 1)' }}>
                            👦 {group.boys} {group.boys === 1 ? 'boy' : 'boys'}
                          </Typography>
                        )}
                        {group.girls > 0 && (
                          <Typography variant="body2" sx={{ color: 'rgba(236, 72, 153, 1)' }}>
                            👧 {group.girls} {group.girls === 1 ? 'girl' : 'girls'}
                          </Typography>
                        )}
                        {group.preferNotToSay > 0 && (
                          <Typography variant="body2" sx={{ color: 'rgba(156, 163, 175, 1)' }}>
                            ⚪ {group.preferNotToSay} prefer not to say
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  );
                })}
              </Box>

              {/* Overall Gender Summary */}
              <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>Overall Gender Summary</Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Paper sx={{ p: 2, flex: 1, backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Total Boys
                  </Typography>
                  <Typography variant="h4" sx={{ color: 'white' }}>👦 {totalBoys}</Typography>
                </Paper>
                <Paper sx={{ p: 2, flex: 1, backgroundColor: 'rgba(236, 72, 153, 0.1)', border: '1px solid rgba(236, 72, 153, 0.3)' }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Total Girls
                  </Typography>
                  <Typography variant="h4" sx={{ color: 'white' }}>👧 {totalGirls}</Typography>
                </Paper>
                <Paper sx={{ p: 2, flex: 1, backgroundColor: 'rgba(156, 163, 175, 0.1)', border: '1px solid rgba(156, 163, 175, 0.3)' }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Prefer Not to Say
                  </Typography>
                  <Typography variant="h4" sx={{ color: 'white' }}>⚪ {totalPreferNotToSay}</Typography>
                </Paper>
              </Box>

              <TableContainer sx={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.1)' }}>Counsellor</TableCell>
                      <TableCell sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.1)' }}>Email</TableCell>
                      <TableCell align="center" sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.1)' }}>Entries</TableCell>
                      <TableCell sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.1)' }}>Last Updated</TableCell>
                      <TableCell sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.1)' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {submissions.submissions.map((sub) => (
                      <TableRow key={sub.uniqueId}>
                        <TableCell sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.1)' }}>{sub.counsellorName}</TableCell>
                        <TableCell sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.1)' }}>{sub.counsellorEmail || '-'}</TableCell>
                        <TableCell align="center" sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                          <Chip
                            label={sub.entriesCount}
                            color={sub.entriesCount > 0 ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                          {sub.entriesCount > 0
                            ? new Date(sub.lastUpdated).toLocaleString()
                            : '-'}
                        </TableCell>
                        <TableCell sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                          {sub.entriesCount > 0 && (
                            <Button
                              size="small"
                              onClick={() => viewDetails(sub)}
                              sx={{ color: '#60a5fa' }}
                            >
                              View Details
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
            );
          })()}
        </Paper>
      )}

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedSubmission?.counsellorName} - Submissions
        </DialogTitle>
        <DialogContent>
          {selectedSubmission?.entries.map((entry, index) => (
            <Paper key={index} sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" color="primary">Entry {index + 1}</Typography>
              <Typography><strong>Client:</strong> {entry.clientName}</Typography>
              <Typography><strong>Child:</strong> {entry.childName} ({entry.childAge} years old, {entry.childGender === 'prefer-not-to-say' ? 'Prefer not to say' : entry.childGender.charAt(0).toUpperCase() + entry.childGender.slice(1)})</Typography>
              <Typography><strong>Toy Preference:</strong> {entry.toyPreference}</Typography>
            </Paper>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChristmasAdmin;
